import React, { useState, useMemo } from 'react';
import { SecondaryBtn, CollapsibleSection } from './systemKit';
import DismissAlertModal from './DismissAlertModal';
import { describeAlert, formatDetectionAge } from '../lib/shrinkflation';

// ─── VendorWatch ────────────────────────────────────────────────────────────
// Vendor Watch section for Price Tracker — durable, lifecycle-tracked
// shrinkflation alerts. Per DESIGN_SYSTEM.md §Vendor Watch:
//   - Active alerts: 3px left bar + tinted card (meaning-driven KPI card pattern)
//   - Watching alerts: plain card, no left bar, calmer treatment
//   - Section collapses entirely when zero alerts (no "all good" empty state)
//
// As of 2026-04-27 the entire section sits inside <CollapsibleSection> so
// the operator sees a glanceable subhead by default and only expands when
// they want the full alert cards. See DESIGN_SYSTEM.md §Collapsible content
// sections for the convention.

// Shorten a normalized food_item name for the compact subhead. Strip
// vendor prefixes ("TM SGC ") and trailing parenthetical units ("(case)"),
// take the first 2-3 words. The goal is "operator instantly recognizes
// the item" — not a faithful copy of the canonical name.
function shortName(name) {
  if (!name) return 'Item';
  let s = String(name)
    .replace(/\(.*?\)/g, '')      // strip trailing "(case)" / "(lb)"
    .replace(/\s{2,}/g, ' ')
    .trim();
  // Drop vendor prefix words (any all-caps token of length 2-4 at the
  // start that's followed by another word) — e.g. "SGC BEEF BRISKET" →
  // "BEEF BRISKET". Conservative heuristic; safe to leave the original
  // alone if no match.
  s = s.replace(/^([A-Z]{2,4}\s+){1,2}(?=[A-Z])/, '');
  // First 3 words.
  const words = s.split(/\s+/).slice(0, 3);
  // Title-case (uppercase original is hard to read at body-text size).
  return words
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// Compact delta text + tone for the subhead. Sign-aware, no decimals
// when the number is ≥ 10. pack_count_drop signal renders as the "50# →
// 45#" mini-detail.
function subheadDeltaForAlert(alert) {
  if (!alert) return null;
  if (alert.signal_type === 'pack_count_drop') {
    const from = Math.round(Number(alert.baseline_value));
    const to = Math.round(Number(alert.current_value));
    return { text: `${from}# \u2192 ${to}#`, tone: 'amber', score: Math.abs(from - to) * 100 };
  }
  if (alert.signal_type === 'per_lb_spike') {
    const pct = Number(alert.change_pct);
    if (!isFinite(pct)) return null;
    const sign = pct >= 0 ? '+' : '\u2212';
    const abs = Math.abs(pct);
    const formatted = abs >= 10 ? abs.toFixed(0) : abs.toFixed(1);
    return {
      text: `${sign}${formatted}%`,
      tone: pct >= 0 ? 'critical' : 'success',
      score: abs,
    };
  }
  return null;
}

// Build the subhead React node. Top 3 by severity, append "+N more" if
// truncated. Severity = absolute delta value (per_lb_spike) or pack-size
// delta in pounds (pack_count_drop). Active alerts always rank above
// watching ones — we don't surface "watching" rows in the subhead unless
// there are no active ones at all (in which case watching is what
// matters).
function buildSubhead(active, watching, itemsById) {
  const pool = (active.length > 0 ? active : watching).slice();
  if (pool.length === 0) return null;
  const ranked = pool
    .map(a => ({ alert: a, ...subheadDeltaForAlert(a), name: shortName(itemsById[a.food_item_id]?.normalized_name) }))
    .filter(x => x.text)
    .sort((a, b) => (b.score || 0) - (a.score || 0));

  const TOP = 3;
  const visible = ranked.slice(0, TOP);
  const overflow = ranked.length - visible.length;
  const total = pool.length; // includes those with no delta we could format
  const overflowSuffix = overflow > 0
    ? overflow
    : (total > visible.length ? total - visible.length : 0);

  const colorFor = (tone) =>
    tone === 'critical' ? 'var(--text-critical)'
    : tone === 'success' ? 'var(--text-success)'
    : tone === 'amber' ? 'var(--text-warning)'
    : 'var(--text-secondary)';

  return (
    <>
      {visible.map((v, i) => (
        <React.Fragment key={v.alert.id}>
          {i > 0 && <span style={{ color: 'var(--text-muted)', margin: '0 6px' }}>{'\u00B7'}</span>}
          <span style={{ color: 'var(--text-secondary)' }}>{v.name}</span>
          {' '}
          <span style={{ color: colorFor(v.tone), fontWeight: 600 }}>{v.text}</span>
        </React.Fragment>
      ))}
      {overflowSuffix > 0 && (
        <>
          <span style={{ color: 'var(--text-muted)', margin: '0 6px' }}>{'\u00B7'}</span>
          <span style={{ color: 'var(--text-muted)' }}>+{overflowSuffix} more</span>
        </>
      )}
    </>
  );
}

function AlertCard({ alert, item, isActive, onView, onDismiss }) {
  const vendor = item?.vendor_name ? String(item.vendor_name).replace(/\n/g, ' \u00B7 ') : null;
  return (
    <div
      className="rounded-lg p-3"
      style={{
        background: isActive ? 'var(--bg-warning-subtle)' : 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderLeft: isActive ? '3px solid var(--text-warning)' : '1px solid var(--border)',
      }}
    >
      <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
        {item?.normalized_name || `Item #${alert.food_item_id}`}
      </div>
      <div className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
        {describeAlert(alert)}
      </div>
      <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
        {isActive
          ? <>Detected {formatDetectionAge(alert.first_detected_at)}{vendor ? ` \u00B7 ${vendor}` : ''}</>
          : <>Awaiting next invoice for confirmation{vendor ? ` \u00B7 ${vendor}` : ''}</>
        }
      </div>
      <div className="flex items-center justify-end gap-2 mt-2">
        <SecondaryBtn onClick={() => onView?.(alert)} className="!px-3 !py-1.5 !text-xs">View item</SecondaryBtn>
        <button
          onClick={() => onDismiss?.(alert)}
          className="text-xs px-3 py-1.5 rounded-lg transition-colors"
          style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 500 }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// Warning-triangle icon — matches the existing inline detection icon.
const WarningIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
    <circle cx="12" cy="12" r="10" />
  </svg>
);

/**
 * VendorWatch — top-of-Price-Tracker alert section.
 *
 * Props:
 *   alerts: [{ id, food_item_id, signal_type, status, baseline_value, current_value,
 *              change_pct, first_detected_at, ... }] — null while loading.
 *   itemsById: { [food_item_id]: { id, normalized_name, vendor_name, ... } }
 *   onViewItem: (food_item_id) => void
 *   onAlertChange: () => void  — refresh callback after dismiss
 *   onRunCheck?: () => Promise<{ wouldFire: [...] }> — dry-run trigger
 */
export default function VendorWatch({ alerts, itemsById, onViewItem, onAlertChange, onRunCheck }) {
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [running, setRunning] = useState(false);

  const loading = alerts == null;
  const active = useMemo(() => (alerts || []).filter(a => a.status === 'active'), [alerts]);
  const watching = useMemo(() => (alerts || []).filter(a => a.status === 'watching'), [alerts]);
  const total = active.length + watching.length;

  const subhead = useMemo(
    () => (loading ? null : buildSubhead(active, watching, itemsById)),
    [loading, active, watching, itemsById]
  );

  // Empty state when not loading and no alerts and no dry-run result on
  // screen. Keeps the section in DOM (stable layout) but renders muted +
  // non-clickable.
  const isEmpty = !loading && total === 0 && !dryRunResult;

  async function handleRunCheck(e) {
    if (!onRunCheck) return;
    e?.stopPropagation?.();
    setRunning(true);
    try {
      const res = await onRunCheck();
      setDryRunResult(res || { wouldFire: [] });
    } finally {
      setRunning(false);
    }
  }

  const headerAction = onRunCheck ? (
    <button
      onClick={handleRunCheck}
      disabled={running}
      className="text-xs disabled:opacity-50"
      style={{
        color: 'var(--text-secondary)',
        background: 'transparent', border: 'none',
        cursor: running ? 'wait' : 'pointer',
        textDecoration: 'underline',
      }}
      title="Re-evaluate detection logic against current data without writing to the alert table."
    >
      {running ? 'Checking\u2026' : 'Run alert check'}
    </button>
  ) : null;

  const countLabel = active.length > 0
    ? `${active.length} active${watching.length > 0 ? ` \u00B7 ${watching.length} watching` : ''}`
    : (watching.length > 0 ? `${watching.length} watching` : null);

  return (
    <>
      {/* data-vendor-watch-section is the scrollIntoView target used by
          InventoryTrends and FoodPriceTracker. CollapsibleSection is a
          function component so React would swallow the data-* prop;
          anchor it on a wrapper div instead. */}
      <div data-vendor-watch-section>
      <CollapsibleSection
        id="vendor-watch"
        icon={WarningIcon}
        title="Vendor Watch"
        count={countLabel}
        countTone={active.length > 0 ? 'amber' : 'neutral'}
        subhead={subhead}
        headerAction={isEmpty ? null : headerAction}
        loading={loading}
        empty={isEmpty ? 'No active alerts' : false}
        defaultExpanded={false}
      >
        {active.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center gap-1.5 mb-2" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-warning)' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 9v4"/><path d="M12 17h.01"/><circle cx="12" cy="12" r="10"/></svg>
              Active
            </div>
            <div className="space-y-2">
              {active.map(a => (
                <AlertCard
                  key={a.id}
                  alert={a}
                  item={itemsById[a.food_item_id]}
                  isActive
                  onView={() => onViewItem?.(a.food_item_id)}
                  onDismiss={() => setDismissTarget(a)}
                />
              ))}
            </div>
          </div>
        )}

        {watching.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-2" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Watching <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{'(1 invoice signal \u2014 needs confirmation)'}</span>
            </div>
            <div className="space-y-2">
              {watching.map(a => (
                <AlertCard
                  key={a.id}
                  alert={a}
                  item={itemsById[a.food_item_id]}
                  isActive={false}
                  onView={() => onViewItem?.(a.food_item_id)}
                  onDismiss={() => setDismissTarget(a)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Dry-run result panel — power-user check, modeled into UI per spec
            so users don't need dev tools to verify detection logic. */}
        {dryRunResult && (
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>
                Alert check (dry-run, no writes)
              </div>
              <button
                onClick={() => setDryRunResult(null)}
                className="text-xs"
                style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                {'\u2715'}
              </button>
            </div>
            {(!dryRunResult.wouldFire || dryRunResult.wouldFire.length === 0) ? (
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Detection ran. Zero would-fire alerts at current thresholds.
              </div>
            ) : (
              <div className="space-y-1">
                {dryRunResult.wouldFire.map((p, i) => (
                  <div key={i} className="text-xs p-2 rounded-md" style={{ background: 'var(--bg-subtle)', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                    <span className="font-semibold">{p.item_name}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {' \u2014 '}{p.signal_type} {p.would_promote ? '(would promote \u2192 active)' : `(${p.status})`}{', baseline '}{Number(p.baseline_value).toFixed(2)}{' \u2192 '}{Number(p.current_value).toFixed(2)} ({Number(p.change_pct).toFixed(1)}%)
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CollapsibleSection>
      </div>

      {dismissTarget && (
        <DismissAlertModal
          alert={dismissTarget}
          onClose={() => setDismissTarget(null)}
          onDismissed={() => { setDismissTarget(null); onAlertChange?.(); }}
        />
      )}
    </>
  );
}
